import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './ContactModal.css';

// ─────────────────────────────────────────────────────────────────────────────
// CONFIG
// ─────────────────────────────────────────────────────────────────────────────
//
// WEB3FORMS — paste your access key here (check Gmail draft for setup steps)
// One-time setup: web3forms.com → enter creative@sahilsaini.com.au → copy key
//
const WEB3FORMS_KEY = 'd21797dd-165a-43e6-a4a8-5cfc7a910cb5';

//
// GOOGLE SHEETS (optional — auto-logs every submission to a spreadsheet)
//   1. Create a Google Sheet: columns = Date | Name | Mobile | Email | Brief
//   2. Extensions → Apps Script → paste + deploy as web app:
//
//      function doPost(e) {
//        const d = JSON.parse(e.postData.contents);
//        SpreadsheetApp.getActiveSpreadsheet().getActiveSheet()
//          .appendRow([d.submitted_at, d.name, d.mobile, d.email, d.message]);
//        return ContentService.createTextOutput('ok');
//      }
//
//   3. Deploy → "Execute as: Me" / "Who can access: Anyone" → copy URL below
//
const SHEETS_URL = ''; // ← paste Apps Script web app URL here

// ─────────────────────────────────────────────────────────────────────────────
// AUTO-REPLY EMAIL HTML
// ─────────────────────────────────────────────────────────────────────────────
const AUTOREPLY_HTML = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1.0">
  <link href="https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:wght@300;400;500&display=swap" rel="stylesheet">
</head>
<body style="margin:0;padding:0;background:#080808;font-family:'DM Sans',Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#080808;padding:40px 20px;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;">

        <tr><td style="padding:0 0 40px 0;border-bottom:1px solid rgba(201,168,76,0.25);">
          <p style="margin:0;font-family:'DM Serif Display',Georgia,serif;font-size:22px;color:#f0ece3;letter-spacing:-0.02em;">SS</p>
        </td></tr>

        <tr><td style="padding:48px 0 24px 0;">
          <h1 style="margin:0;font-family:'DM Serif Display',Georgia,serif;font-size:36px;font-weight:400;color:#f0ece3;line-height:1.15;letter-spacing:-0.02em;">
            Brief received.<br><span style="color:#c9a84c;">I'll read every word.</span>
          </h1>
        </td></tr>

        <tr><td style="padding:0 0 40px 0;">
          <p style="margin:0 0 20px 0;font-size:15px;font-weight:300;color:#857f77;line-height:1.8;">Thanks for reaching out. Your brief is in and I've got it.</p>
          <p style="margin:0 0 20px 0;font-size:15px;font-weight:300;color:#857f77;line-height:1.8;">I read every brief personally — not a team, not an assistant. I'll come back to you within 24 hours with a real response, not a templated one.</p>
          <p style="margin:0;font-size:15px;font-weight:300;color:#857f77;line-height:1.8;">If anything urgent comes up in the meantime, reply directly to this email or call me on the number below.</p>
        </td></tr>

        <tr><td style="padding:0 0 40px 0;">
          <table width="100%" cellpadding="0" cellspacing="0"><tr>
            <td style="height:1px;background:rgba(201,168,76,0.2);font-size:0;line-height:0;">&nbsp;</td>
          </tr></table>
        </td></tr>

        <tr><td style="padding:0 0 48px 0;">
          <p style="margin:0 0 4px 0;font-family:'DM Serif Display',Georgia,serif;font-size:20px;font-weight:400;color:#f0ece3;letter-spacing:-0.01em;">Sahil Saini</p>
          <p style="margin:0 0 20px 0;font-size:11px;font-weight:500;color:#c9a84c;letter-spacing:0.18em;text-transform:uppercase;">Art Director &amp; Creative Lead</p>
          <p style="margin:0 0 6px 0;font-size:13px;font-weight:300;color:#857f77;"><a href="mailto:creative@sahilsaini.com.au" style="color:#857f77;text-decoration:none;">creative@sahilsaini.com.au</a></p>
          <p style="margin:0 0 6px 0;font-size:13px;font-weight:300;color:#857f77;"><a href="tel:+61415523815" style="color:#857f77;text-decoration:none;">+61 415 523 815</a></p>
          <p style="margin:0;font-size:13px;font-weight:300;color:#857f77;"><a href="https://sahilsaini.com.au" style="color:#c9a84c;text-decoration:none;">sahilsaini.com.au</a></p>
        </td></tr>

        <tr><td style="border-top:1px solid rgba(255,255,255,0.05);padding:24px 0 0 0;">
          <p style="margin:0;font-size:10px;color:#3e3a35;letter-spacing:0.06em;text-transform:uppercase;">Melbourne, Australia &nbsp;·&nbsp; Available for senior creative leadership &amp; freelance</p>
        </td></tr>

      </table>
    </td></tr>
  </table>
</body>
</html>`;


// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const STORAGE_KEY = 'ss_contact_submissions';

function saveLocally(data) {
  try {
    const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    existing.push(data);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
  } catch (_) {}
}

export function exportSubmissionsAsCSV() {
  const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  if (!data.length) { alert('No submissions saved yet.'); return; }
  const headers = ['Date', 'Name', 'Mobile', 'Email', 'Brief'];
  const rows    = data.map(d => [d.submitted_at, d.name, d.mobile, d.email, d.message]);
  const csv     = [headers, ...rows]
    .map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const a = Object.assign(document.createElement('a'), {
    href:     URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' })),
    download: `briefs-${new Date().toISOString().split('T')[0]}.csv`,
  });
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// Builds a Google Calendar "Add Event" link for the next working day at 10am
function buildCalendarLink(name, email) {
  const d = new Date();
  // Advance to next weekday
  d.setDate(d.getDate() + 1);
  if (d.getDay() === 6) d.setDate(d.getDate() + 2); // skip Saturday
  if (d.getDay() === 0) d.setDate(d.getDate() + 1); // skip Sunday
  d.setHours(10, 0, 0, 0);
  const end = new Date(d.getTime() + 30 * 60000);
  const fmt = dt => [
    dt.getFullYear(),
    String(dt.getMonth() + 1).padStart(2, '0'),
    String(dt.getDate()).padStart(2, '0'),
    'T',
    String(dt.getHours()).padStart(2, '0'),
    String(dt.getMinutes()).padStart(2, '0'),
    '00',
  ].join('');
  const params = new URLSearchParams({
    action:  'TEMPLATE',
    text:    `Review Brief — ${name}`,
    dates:   `${fmt(d)}/${fmt(end)}`,
    details: `Review portfolio brief from ${name} (${email}).\n\nReply within 24 hours.`,
  });
  return `https://calendar.google.com/calendar/render?${params}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// ContactModal
// ─────────────────────────────────────────────────────────────────────────────
const EASE  = [0.22, 1, 0.36, 1];
const EMPTY = { name: '', mobile: '', email: '', message: '' };

export default function ContactModal({ open, onClose, prefill = '' }) {
  const [form,   setForm]   = useState(EMPTY);
  const [status, setStatus] = useState('idle'); // idle | sending | success

  // When the modal opens (or prefill changes), inject the prefill into message
  useEffect(() => {
    if (open) {
      const safe = typeof prefill === 'string' ? prefill : '';
      setForm(f => ({ ...f, message: safe || f.message }));
    }
  }, [open, prefill]);

  const close = useCallback(() => {
    if (status === 'sending') return;
    onClose();
    setTimeout(() => { setForm(EMPTY); setStatus('idle'); }, 450);
  }, [status, onClose]);

  useEffect(() => {
    if (!open) return;
    const fn = e => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [open, close]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setStatus('sending');

    const calLink = buildCalendarLink(form.name, form.email);
    const now     = new Date().toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' });

    const payload = { ...form, submitted_at: now };
    saveLocally(payload);

    const jobs = [];

    // Web3Forms — sends real email to creative@sahilsaini.com.au + auto-reply to sender
    if (WEB3FORMS_KEY) {
      jobs.push(
        fetch('https://api.web3forms.com/submit', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            access_key:   WEB3FORMS_KEY,
            subject:      `New Brief — ${form.name}`,
            from_name:    'Sahil Saini',
            name:         form.name,
            email:        form.email,
            phone:        form.mobile || '—',
            message:      form.message,
            submitted_at: now,
            review_calendar_link: calLink,
            botcheck:     '',
            // Auto-reply to the person who submitted
            replyto:      form.email,
            autoresponse: true,
            autoresponse_subject: 'Brief received — I\'ll be in touch shortly.',
            autoresponse_message: AUTOREPLY_HTML,
          }),
        }),
      );
    }

    // Google Sheets log
    if (SHEETS_URL) {
      jobs.push(
        fetch(SHEETS_URL, {
          method: 'POST',
          body:   JSON.stringify(payload),
        }),
      );
    }

    await Promise.allSettled(jobs);
    setStatus('success');
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="cm-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          onClick={close}
        >
          <motion.div
            className="cm-panel"
            initial={{ opacity: 0, scale: 0.955, y: 28 }}
            animate={{ opacity: 1, scale: 1,     y: 0  }}
            exit={{    opacity: 0, scale: 0.965,  y: 12 }}
            transition={{ duration: 0.52, ease: EASE }}
            onClick={e => e.stopPropagation()}
          >
            <button className="cm-close" onClick={close} aria-label="Close">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6"  y1="6" x2="18" y2="18"/>
              </svg>
            </button>

            {/* Left */}
            <div className="cm-left">
              <span className="cm-logo">SS</span>
              <div className="cm-copy">
                <motion.h2
                  className="cm-heading"
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.12, ease: EASE }}
                >
                  Tell me what<br />you're building.
                </motion.h2>
                <motion.p
                  className="cm-sub"
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.22, ease: EASE }}
                >
                  Share your brief, goals, and timeline. I'll read everything
                  and respond personally — usually within 24 hours.
                </motion.p>
              </div>
              <motion.div
                className="cm-details"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.36 }}
              >
                <p>Open to senior creative leadership roles in Melbourne — Creative Lead, Head of Creative, Art Director.</p>
                <p>Available for freelance creative direction and brand system work.</p>
                <a href="mailto:creative@sahilsaini.com.au" className="cm-email">
                  creative@sahilsaini.com.au
                </a>
                <a href="tel:+61415523815" className="cm-phone">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.87a16 16 0 0 0 6.06 6.06l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7a2 2 0 0 1 1.72 2.03z"/>
                  </svg>
                  +61 415 523 815
                </a>
              </motion.div>
            </div>

            {/* Right */}
            <div className="cm-right">
              <AnimatePresence mode="wait">
                {status === 'success'
                  ? <SuccessState key="success" name={form.name} email={form.email} onClose={close} />
                  : <FormState   key="form"    form={form} status={status} onChange={handleChange} onSubmit={handleSubmit} />
                }
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ── Form ──────────────────────────────────────────────────────────────────────
function FormState({ form, status, onChange, onSubmit }) {
  return (
    <motion.form
      className="cm-form"
      onSubmit={onSubmit}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.38, ease: EASE }}
    >
      <div className="cm-row">
        <Field id="name"   label="Full Name"      type="text" value={form.name}
          onChange={onChange} autoComplete="name" placeholder="Your name" required />
        <Field id="mobile" label="Mobile Number"  type="tel"  value={form.mobile}
          onChange={onChange} autoComplete="tel"  placeholder="+61 4xx xxx xxx" />
      </div>

      <Field id="email" label="Email Address" type="email" value={form.email}
        onChange={onChange} autoComplete="email" placeholder="you@company.com" required />

      <div className="cf-field cf-field--grow">
        <label className="cf-label" htmlFor="message">What's your brief?</label>
        <textarea
          id="message" name="message"
          className="cf-input cf-textarea"
          value={form.message}
          onChange={onChange}
          required rows={7}
          placeholder="Describe the project, your goals, and any timing. The more context, the better."
        />
      </div>

      <div className="cm-form-footer">
        <button type="submit" className="cm-submit" disabled={status === 'sending'}>
          {status === 'sending' ? <SendingDots /> : 'Send Brief →'}
        </button>
        <span className="cm-privacy">I'll never share your details.</span>
      </div>
    </motion.form>
  );
}

function Field({ id, label, type, value, onChange, autoComplete, placeholder, required }) {
  return (
    <div className="cf-field">
      <label className="cf-label" htmlFor={id}>{label}</label>
      <input id={id} name={id} type={type} className="cf-input"
        value={value} onChange={onChange}
        autoComplete={autoComplete} placeholder={placeholder} required={required} />
    </div>
  );
}

function SendingDots() {
  const [dots, setDots] = useState('');
  useEffect(() => {
    const id = setInterval(() => setDots(d => (d.length >= 3 ? '' : d + '.')), 380);
    return () => clearInterval(id);
  }, []);
  return <span>Sending{dots}</span>;
}

// ── Success ───────────────────────────────────────────────────────────────────
function SuccessState({ name, email, onClose }) {
  const calLink = buildCalendarLink(name, email);
  return (
    <motion.div
      className="cm-success"
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5, ease: EASE }}
    >
      <motion.div
        className="cm-success-ring"
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1,   opacity: 1 }}
        transition={{ duration: 0.55, delay: 0.1, ease: EASE }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </motion.div>

      <motion.h3
        className="cm-success-heading"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.22, ease: EASE }}
      >
        Brief received.
      </motion.h3>

      <motion.p
        className="cm-success-sub"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.32, ease: EASE }}
      >
        Thanks — your brief is in. I'll be in touch shortly.
      </motion.p>


<motion.button
        className="cm-success-btn"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.54 }}
      >
        Close
      </motion.button>
    </motion.div>
  );
}
